import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, PieChart, TrendingUp, MapPin, Target,
  Download, RefreshCw, Lock, Eye, EyeOff, Filter, 
  Lightbulb, CheckCircle, ArrowLeft, AlertTriangle, Users,
  Briefcase, Scale, Map, Compass, Activity, Zap, Award, GitBranch,
  ArrowRight, ChevronRight, TrendingDown, Globe, FileText, Calendar
} from 'lucide-react';
import axios from 'axios';
import {
  BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Sankey, Treemap
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin password
const ADMIN_PASSWORD = "declic2025";

// Color palette
const COLORS = ['#A4187A', '#FB6823', '#2196F3', '#4CAF50', '#E91E63', '#9C27B0', '#00BCD4', '#FFC107', '#795548', '#607D8B'];
const DOMAIN_COLORS = {
  'Santé': '#4CAF50',
  'health': '#4CAF50',
  'Technologie': '#2196F3',
  'technology': '#2196F3',
  'Créativité': '#E91E63',
  'creativity': '#E91E63',
  'Social': '#FB6823',
  'social': '#FB6823',
  'Éducation': '#9C27B0',
  'education': '#9C27B0',
  'teaching': '#9C27B0',
  'Business': '#795548',
  'business': '#795548',
  'entrepreneurship': '#795548',
  'Science': '#00BCD4',
  'science': '#00BCD4',
  'Technique': '#607D8B',
  'technical': '#607D8B',
  'Gestion & Organisation': '#FFC107',
  'Administration': '#FFC107',
  'administration': '#FFC107',
  'Sport & Bien-être': '#F44336',
  'Sport': '#F44336',
  'sports': '#F44336'
};

export default function NationalObservatory() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('underexplored');
  const [dashboardLang, setDashboardLang] = useState('fr');
  const [lastUpdated, setLastUpdated] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      fetchData(dashboardLang);
    } else {
      setAuthError(dashboardLang === 'fr' ? 'Mot de passe incorrect' : 'Onjuist wachtwoord');
    }
  };

  const fetchData = async (lang = dashboardLang, extended = true) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch extended data with Parts 6-9
      const response = await axios.get(`${API}/analytics/observatory?lang=${lang}&extended=${extended}`);
      setData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(lang === 'fr' ? 'Erreur lors du chargement des données' : 'Fout bij het laden van gegevens');
      console.error('Error fetching observatory data:', err);
    }
    setLoading(false);
  };

  const handleLanguageChange = (newLang) => {
    setDashboardLang(newLang);
    if (isAuthenticated) {
      fetchData(newLang);
    }
  };

  const [exportingPdf, setExportingPdf] = useState(false);
  
  const exportPDF = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/analytics/observatory/export-pdf?lang=${dashboardLang}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `declic-observatoire-national-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      alert(dashboardLang === 'fr' 
        ? 'Erreur lors de la génération du PDF' 
        : 'Fout bij het genereren van de PDF');
    }
    setExportingPdf(false);
  };

  const getLabel = (path) => {
    if (!data?.ui_labels) return path;
    const keys = path.split('.');
    let value = data.ui_labels;
    for (const key of keys) {
      value = value?.[key];
    }
    return value || path;
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1D1D1D] to-[#A4187A] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="flex justify-center gap-2 mb-6">
            <button
              onClick={() => handleLanguageChange('fr')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dashboardLang === 'fr'
                  ? 'bg-[#A4187A] text-white'
                  : 'bg-[#F3E5F5] text-[#A4187A] hover:bg-[#E1BEE7]'
              }`}
            >
              FR
            </button>
            <button
              onClick={() => handleLanguageChange('nl')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dashboardLang === 'nl'
                  ? 'bg-[#FB6823] text-white'
                  : 'bg-[#FFF3E0] text-[#FB6823] hover:bg-[#FFE0B2]'
              }`}
            >
              NL
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#A4187A] to-[#FB6823] flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1D1D1D]">
              {dashboardLang === 'fr' ? 'Observatoire National' : 'Nationaal Observatorium'}
            </h1>
            <p className="text-[#1D1D1D]/60 text-sm mt-2">
              {dashboardLang === 'fr' ? 'Aspirations Professionnelles des Jeunes' : 'Loopbaanambities van Jongeren'}
            </p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={dashboardLang === 'fr' ? 'Mot de passe' : 'Wachtwoord'}
                className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                data-testid="observatory-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1D1D1D]/40"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {authError && (
              <p className="text-red-500 text-sm mb-4">{authError}</p>
            )}
            
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-[#A4187A] to-[#FB6823] text-white rounded-xl font-bold hover:shadow-lg transition-all"
              data-testid="observatory-login-btn"
            >
              {dashboardLang === 'fr' ? 'Accéder à l\'Observatoire' : 'Naar Observatorium'}
            </button>
          </form>
          
          <button
            onClick={() => navigate('/analytics')}
            className="w-full mt-4 py-2 text-[#A4187A] text-sm hover:underline flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {dashboardLang === 'fr' ? 'Retour au Dashboard Analytics' : 'Terug naar Analytics Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#A4187A] animate-spin mx-auto mb-4" />
          <p className="text-[#1D1D1D]/60">Chargement de l'Observatoire...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={() => fetchData()} className="px-4 py-2 bg-[#A4187A] text-white rounded-lg">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-[#EBEBEB]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1D1D1D] via-[#A4187A] to-[#FB6823] text-white py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/analytics')}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Retour"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Globe className="w-6 h-6" />
                  {getLabel('title')}
                </h1>
                <p className="text-white/70 text-sm">{getLabel('subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <div className="flex bg-white/20 rounded-lg p-1 mr-2">
                <button
                  onClick={() => handleLanguageChange('fr')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dashboardLang === 'fr' ? 'bg-white text-[#A4187A]' : 'text-white/80 hover:text-white'
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => handleLanguageChange('nl')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dashboardLang === 'nl' ? 'bg-white text-[#FB6823]' : 'text-white/80 hover:text-white'
                  }`}
                >
                  NL
                </button>
              </div>
              
              {lastUpdated && (
                <span className="text-xs text-white/70">
                  {lastUpdated.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              
              {/* Plaidoyer Link Button */}
              <button
                onClick={() => navigate('/plaidoyer')}
                className="px-3 py-2 bg-[#FB6823] rounded-lg hover:bg-[#E55A1B] transition-colors flex items-center gap-2 font-medium"
                title={dashboardLang === 'fr' ? 'Voir le Plaidoyer Déclic' : 'Bekijk Déclic Pleidooi'}
                data-testid="observatory-plaidoyer-btn"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Plaidoyer</span>
              </button>
              
              {/* PDF Export Button */}
              <button
                onClick={exportPDF}
                disabled={exportingPdf}
                className="px-3 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                title={dashboardLang === 'fr' ? 'Télécharger le rapport PDF' : 'Download PDF rapport'}
                data-testid="observatory-export-pdf-btn"
              >
                {exportingPdf ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span className="hidden sm:inline text-sm">PDF</span>
              </button>
              
              <button
                onClick={() => fetchData(dashboardLang)}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Actualiser"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { id: 'underexplored', label: getLabel('tabs.underexplored'), icon: AlertTriangle },
              { id: 'diversity', label: getLabel('tabs.diversity'), icon: Scale },
              { id: 'market', label: getLabel('tabs.market'), icon: Briefcase },
              { id: 'journey', label: getLabel('tabs.journey'), icon: GitBranch },
              { id: 'impact', label: getLabel('tabs.impact'), icon: Zap },
              { id: 'cross', label: dashboardLang === 'fr' ? 'Analyses croisées' : 'Kruisanalyses', icon: Filter },
              { id: 'temporal', label: dashboardLang === 'fr' ? 'Tendances' : 'Trends', icon: TrendingUp },
              { id: 'recommendations', label: dashboardLang === 'fr' ? 'Recommandations' : 'Aanbevelingen', icon: Lightbulb }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-[#A4187A] font-medium'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
                data-testid={`observatory-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4 space-y-6">
        
        {/* PART 1: Under-explored Careers Tab */}
        {activeTab === 'underexplored' && data?.underexplored_careers && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                icon={Target}
                label="Métiers dans la base"
                value={data.underexplored_careers.total_careers || 0}
                color="#A4187A"
              />
              <MetricCard
                icon={AlertTriangle}
                label="Jamais suggérés"
                value={data.underexplored_careers.total_never_suggested || 0}
                color="#F44336"
              />
              <MetricCard
                icon={TrendingDown}
                label="Rarement suggérés"
                value={data.underexplored_careers.total_rarely_suggested || 0}
                color="#FB6823"
              />
              <MetricCard
                icon={CheckCircle}
                label="Taux de couverture"
                value={`${data.underexplored_careers.coverage_rate || 0}%`}
                color="#4CAF50"
              />
            </div>

            {/* Never Suggested Careers */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#F44336]" />
                {getLabel('sections.never_suggested')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Métiers présents dans la base mais jamais apparus dans les suggestions)
                </span>
              </h2>
              
              {data.underexplored_careers.never_suggested?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.underexplored_careers.never_suggested.map((career, idx) => (
                    <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="font-medium text-[#1D1D1D]">{career.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(career.domains || []).slice(0, 3).map((domain, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: DOMAIN_COLORS[domain] || '#607D8B' }}
                          >
                            {domain}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Tous les métiers ont été suggérés au moins une fois</p>
              )}
            </section>

            {/* Rarely Suggested */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-[#FB6823]" />
                {getLabel('sections.rarely_suggested')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Suggérés moins de 3 fois)
                </span>
              </h2>
              
              {data.underexplored_careers.rarely_suggested?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.underexplored_careers.rarely_suggested.map((career, idx) => (
                    <div key={idx} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="font-medium text-[#1D1D1D]">{career.name}</div>
                        <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">
                          {career.suggested_count}x
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(career.domains || []).slice(0, 3).map((domain, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: DOMAIN_COLORS[domain] || '#607D8B' }}
                          >
                            {domain}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucun métier rarement suggéré</p>
              )}
            </section>

            {/* Low Selection Rate */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#9C27B0]" />
                {getLabel('sections.low_selection')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Souvent suggérés mais rarement choisis pour l'exploration)
                </span>
              </h2>
              
              {data.underexplored_careers.low_selection_rate?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E0E0E0]">
                        <th className="text-left py-2 px-3 text-[#1D1D1D]/60">Métier</th>
                        <th className="text-right py-2 px-3 text-[#1D1D1D]/60">Suggéré</th>
                        <th className="text-right py-2 px-3 text-[#1D1D1D]/60">Sélectionné</th>
                        <th className="text-right py-2 px-3 text-[#1D1D1D]/60">Taux</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.underexplored_careers.low_selection_rate.map((career, idx) => (
                        <tr key={idx} className="border-b border-[#E0E0E0]/50 hover:bg-purple-50">
                          <td className="py-2 px-3 font-medium">{career.name}</td>
                          <td className="py-2 px-3 text-right">{career.suggested_count}</td>
                          <td className="py-2 px-3 text-right">{career.selected_count}</td>
                          <td className="py-2 px-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              career.selection_rate === 0 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {career.selection_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucune donnée disponible</p>
              )}
            </section>
          </>
        )}

        {/* PART 2: Diversity Tab */}
        {activeTab === 'diversity' && data?.diversity_indicators && (
          <>
            {/* Overall Diversity Score */}
            <div className="bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-xl p-6 border border-[#A4187A]/20">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1D1D1D]">Score Global de Diversité</h2>
                  <p className="text-sm text-[#1D1D1D]/60">0% = très biaisé, 100% = parfaitement équilibré</p>
                </div>
                <div className="text-5xl font-bold" style={{
                  color: data.diversity_indicators.overall_diversity_score > 70 ? '#4CAF50' :
                         data.diversity_indicators.overall_diversity_score > 40 ? '#FB6823' : '#F44336'
                }}>
                  {data.diversity_indicators.overall_diversity_score}%
                </div>
              </div>
            </div>

            {/* Gender Diversity by Domain */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#A4187A]" />
                {getLabel('sections.gender_bias')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(data.diversity_indicators.gender_diversity_by_domain || {}).map(([domain, info], idx) => (
                  <div key={idx} className={`rounded-lg p-4 border ${
                    info.bias_alert === 'balanced' ? 'bg-green-50 border-green-200' :
                    info.bias_alert === 'high_male' ? 'bg-blue-50 border-blue-200' :
                    'bg-pink-50 border-pink-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{domain}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        info.bias_alert === 'balanced' ? 'bg-green-200 text-green-800' :
                        info.bias_alert === 'high_male' ? 'bg-blue-200 text-blue-800' :
                        'bg-pink-200 text-pink-800'
                      }`}>
                        {info.bias_alert === 'balanced' ? 'Équilibré' :
                         info.bias_alert === 'high_male' ? 'Majoritairement H' : 'Majoritairement F'}
                      </span>
                    </div>
                    
                    {/* Gender bar */}
                    <div className="flex h-4 rounded-full overflow-hidden bg-gray-200">
                      <div
                        className="bg-blue-500 transition-all"
                        style={{ width: `${info.male_pct}%` }}
                        title={`Hommes: ${info.male_pct}%`}
                      />
                      <div
                        className="bg-pink-500 transition-all"
                        style={{ width: `${info.female_pct}%` }}
                        title={`Femmes: ${info.female_pct}%`}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1 text-[#1D1D1D]/60">
                      <span>H: {info.male_pct}%</span>
                      <span>F: {info.female_pct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Careers with Gender Bias */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#FB6823]" />
                {getLabel('sections.careers_biased')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Plus de 75% d'un genre)
                </span>
              </h2>
              
              {data.diversity_indicators.careers_with_gender_bias?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.diversity_indicators.careers_with_gender_bias.map((career, idx) => (
                    <div key={idx} className={`rounded-lg p-3 border ${
                      career.bias_type === 'male_dominated' ? 'bg-blue-50 border-blue-200' : 'bg-pink-50 border-pink-200'
                    }`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{career.career}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          career.bias_type === 'male_dominated' ? 'bg-blue-200 text-blue-800' : 'bg-pink-200 text-pink-800'
                        }`}>
                          {career.bias_type === 'male_dominated' ? `${career.male_pct}% H` : `${career.female_pct}% F`}
                        </span>
                      </div>
                      <div className="text-xs text-[#1D1D1D]/50 mt-1">
                        {career.total} utilisateurs
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucun biais de genre significatif détecté</p>
              )}
            </section>

            {/* Bias Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="font-medium text-blue-800 mb-2">Domaines à dominance masculine</h3>
                <div className="space-y-1">
                  {(data.diversity_indicators.bias_summary?.male_dominated_domains || []).map((d, i) => (
                    <span key={i} className="inline-block mr-2 px-2 py-0.5 bg-blue-200 text-blue-800 rounded text-sm capitalize">{d}</span>
                  ))}
                  {(!data.diversity_indicators.bias_summary?.male_dominated_domains?.length) && (
                    <span className="text-blue-600 text-sm">Aucun</span>
                  )}
                </div>
              </div>
              
              <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
                <h3 className="font-medium text-pink-800 mb-2">Domaines à dominance féminine</h3>
                <div className="space-y-1">
                  {(data.diversity_indicators.bias_summary?.female_dominated_domains || []).map((d, i) => (
                    <span key={i} className="inline-block mr-2 px-2 py-0.5 bg-pink-200 text-pink-800 rounded text-sm capitalize">{d}</span>
                  ))}
                  {(!data.diversity_indicators.bias_summary?.female_dominated_domains?.length) && (
                    <span className="text-pink-600 text-sm">Aucun</span>
                  )}
                </div>
              </div>
              
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <h3 className="font-medium text-green-800 mb-2">Domaines équilibrés</h3>
                <div className="space-y-1">
                  {(data.diversity_indicators.bias_summary?.balanced_domains || []).map((d, i) => (
                    <span key={i} className="inline-block mr-2 px-2 py-0.5 bg-green-200 text-green-800 rounded text-sm capitalize">{d}</span>
                  ))}
                  {(!data.diversity_indicators.bias_summary?.balanced_domains?.length) && (
                    <span className="text-green-600 text-sm">Aucun</span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* PART 3: Market Comparison Tab */}
        {activeTab === 'market' && data?.market_comparison && (
          <>
            {/* Alignment Score */}
            <div className="bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10 rounded-xl p-6 border border-[#4CAF50]/20">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1D1D1D]">Score d'Alignement Aspirations/Marché</h2>
                  <p className="text-sm text-[#1D1D1D]/60">
                    {data.market_comparison.total_shortage_careers} métiers en pénurie analysés
                  </p>
                </div>
                <div className="text-5xl font-bold" style={{
                  color: data.market_comparison.alignment_score > 50 ? '#4CAF50' :
                         data.market_comparison.alignment_score > 25 ? '#FB6823' : '#F44336'
                }}>
                  {data.market_comparison.alignment_score}%
                </div>
              </div>
            </div>

            {/* High Demand Low Interest */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#F44336]" />
                {getLabel('sections.high_demand')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Opportunités manquées)
                </span>
              </h2>
              
              {data.market_comparison.high_demand_low_interest?.length > 0 ? (
                <div className="space-y-2">
                  {data.market_comparison.high_demand_low_interest.slice(0, 10).map((career, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div>
                        <span className="font-medium">{career.career}</span>
                        <span className="ml-2 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                          PÉNURIE
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-[#1D1D1D]/60">
                          {career.youth_interest} intéressés
                        </span>
                        <div className="w-24 h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-full bg-red-500 rounded-full"
                            style={{ width: `${100 - career.gap_score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucune donnée disponible</p>
              )}
            </section>

            {/* Well Aligned Careers */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#4CAF50]" />
                {getLabel('sections.well_aligned')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Métiers en pénurie populaires chez les jeunes)
                </span>
              </h2>
              
              {data.market_comparison.well_aligned_careers?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.market_comparison.well_aligned_careers.map((career, idx) => (
                    <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <span className="font-medium">{career.career}</span>
                        <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                          {career.youth_interest} intéressés
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucun métier en pénurie n'est populaire</p>
              )}
            </section>

            {/* Popular but Saturated */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#FB6823]" />
                {getLabel('sections.popular_saturated')}
                <span className="ml-2 text-sm font-normal text-[#1D1D1D]/50">
                  (Populaires mais pas en pénurie)
                </span>
              </h2>
              
              {data.market_comparison.popular_but_saturated?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.market_comparison.popular_but_saturated} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="career" type="category" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="youth_interest" fill="#FB6823" name="Intérêt jeunes" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucune donnée disponible</p>
              )}
            </section>
          </>
        )}

        {/* PART 4: Decision Journey Tab */}
        {activeTab === 'journey' && data?.decision_journey && (
          <>
            {/* Key Journey Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                icon={Lightbulb}
                label={getLabel('sections.discovery_rate')}
                value={`${data.decision_journey.discovery_rate || 0}%`}
                color="#4CAF50"
                subtitle="Sans idée → Découverte"
              />
              <MetricCard
                icon={CheckCircle}
                label={getLabel('sections.confirmation_rate')}
                value={`${data.decision_journey.confirmation_rate || 0}%`}
                color="#2196F3"
                subtitle="Idée → Confirmée"
              />
              <MetricCard
                icon={ArrowRight}
                label="Continuation Stage 2"
                value={`${data.decision_journey.stage2_continuation_rate || 0}%`}
                color="#FB6823"
              />
              <MetricCard
                icon={Users}
                label="Utilisent le neutre"
                value={`${data.decision_journey.neutral_usage?.percentage_using_neutral || 0}%`}
                color="#9C27B0"
              />
            </div>

            {/* Engagement Intent Distribution */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Compass className="w-5 h-5 text-[#A4187A]" />
                Intentions d'engagement après le test
              </h2>
              
              {data.decision_journey.engagement_distribution?.length > 0 ? (
                (() => {
                  // Labels for engagement intents
                  const engagementLabels = {
                    'meet_professional': 'Rencontrer un pro',
                    'explore_training': 'Explorer formations',
                    'discuss_advisor': 'Discuter conseiller',
                    'continue_exploring': 'Continuer exploration'
                  };
                  
                  // Aggregate combined intents
                  const aggregatedMap = {};
                  let totalResponses = 0;
                  
                  data.decision_journey.engagement_distribution.forEach(item => {
                    const intent = item.intent || item._id;
                    // Handle combined intents (e.g., "meet_professional,explore_training")
                    const intents = intent?.includes(',') 
                      ? intent.split(',').map(i => i.trim())
                      : [intent];
                    
                    intents.forEach(key => {
                      const label = engagementLabels[key] || key;
                      if (!aggregatedMap[label]) {
                        aggregatedMap[label] = 0;
                      }
                      aggregatedMap[label] += item.count;
                    });
                    totalResponses += item.count;
                  });
                  
                  // Convert to array and sort
                  const aggregatedData = Object.entries(aggregatedMap)
                    .map(([intent, count]) => ({
                      intent,
                      count,
                      percentage: totalResponses > 0 ? ((count / totalResponses) * 100).toFixed(1) : '0.0'
                    }))
                    .sort((a, b) => b.count - a.count);
                  
                  return (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={aggregatedData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="intent" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => `${v}%`} />
                        <Tooltip formatter={(value, name) => [`${value}%`, 'Pourcentage']} />
                        <Bar dataKey="percentage" fill="#A4187A" radius={[4, 4, 0, 0]}>
                          {aggregatedData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Aucune donnée disponible</p>
              )}
            </section>

            {/* Transition Matrix */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-[#FB6823]" />
                Matrice de transition Pré-test → Post-test
              </h2>
              
              {data.decision_journey.transition_matrix && Object.keys(data.decision_journey.transition_matrix).length > 0 ? (
                (() => {
                  // Calculate total for percentages
                  let total = 0;
                  Object.values(data.decision_journey.transition_matrix).forEach(posts => {
                    total += (posts?.discovered_new || 0) + (posts?.confirmed_idea || 0) + 
                             (posts?.new_directions || 0) + (posts?.still_reflecting || 0);
                  });
                  
                  const pct = (val) => total > 0 ? ((val / total) * 100).toFixed(1) : '0.0';
                  
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-[#A4187A]">
                            <th className="py-2 px-3 text-left bg-[#F3E5F5]">Avant le test ↓ / Après →</th>
                            <th className="py-2 px-3 text-center">Découverte</th>
                            <th className="py-2 px-3 text-center">Confirmé</th>
                            <th className="py-2 px-3 text-center">Nouvelles pistes</th>
                            <th className="py-2 px-3 text-center">Réflexion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(data.decision_journey.transition_matrix).map(([pre, posts], idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-2 px-3 font-medium bg-[#F3E5F5]">
                                {pre === 'no_idea' ? 'Pas d\'idée' :
                                 pre === 'some_ideas' ? 'Quelques idées' :
                                 pre === 'clear_idea' ? 'Idée claire' : pre}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {(posts?.discovered_new || 0) > 0 ? `${pct(posts.discovered_new)}%` : '-'}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {(posts?.confirmed_idea || 0) > 0 ? `${pct(posts.confirmed_idea)}%` : '-'}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {(posts?.new_directions || 0) > 0 ? `${pct(posts.new_directions)}%` : '-'}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {(posts?.still_reflecting || 0) > 0 ? `${pct(posts.still_reflecting)}%` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données pour la matrice</p>
              )}
            </section>

            {/* Journey Insights */}
            <div className="bg-gradient-to-r from-[#A4187A]/10 to-[#4CAF50]/10 rounded-xl p-6 border border-[#A4187A]/20">
              <h3 className="font-bold text-[#1D1D1D] mb-3">Points clés du parcours</h3>
              {(() => {
                // Calculate percentages for journey insights
                const totalUsers = (data.decision_journey.journey_insights?.users_discovered_new || 0) +
                                  (data.decision_journey.journey_insights?.users_confirmed_idea || 0);
                const discoveredPct = totalUsers > 0 
                  ? ((data.decision_journey.journey_insights?.users_discovered_new || 0) / totalUsers * 100).toFixed(1)
                  : '0.0';
                const confirmedPct = totalUsers > 0
                  ? ((data.decision_journey.journey_insights?.users_confirmed_idea || 0) / totalUsers * 100).toFixed(1)
                  : '0.0';
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-[#4CAF50]">
                        {discoveredPct}%
                      </div>
                      <div className="text-sm text-[#1D1D1D]/60">ont découvert de nouveaux métiers</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-[#2196F3]">
                        {confirmedPct}%
                      </div>
                      <div className="text-sm text-[#1D1D1D]/60">ont confirmé leur idée</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-[#A4187A]">
                        {data.decision_journey.journey_insights?.top_engagement_intent?.intent || 'N/A'}
                      </div>
                      <div className="text-sm text-[#1D1D1D]/60">intention la plus populaire</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* PART 5: Impact Tab */}
        {activeTab === 'impact' && data?.impact_indicators && (
          <>
            {/* Key Impact Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                icon={Lightbulb}
                label={getLabel('sections.cognitive_expansion')}
                value={`${data.impact_indicators.cognitive_expansion_score || 0}%`}
                color="#4CAF50"
                subtitle="Découverte + Nouvelles pistes"
              />
              <MetricCard
                icon={Activity}
                label={getLabel('sections.exploration_index')}
                value={`${data.impact_indicators.active_exploration_index || 0}`}
                color="#FB6823"
                subtitle="Engagement Stage 2"
              />
              <MetricCard
                icon={Users}
                label="Utilisateurs impactés"
                value={data.impact_indicators.impact_summary?.total_users_with_impact || 0}
                color="#A4187A"
              />
              <MetricCard
                icon={Award}
                label="Découvertes"
                value={data.impact_indicators.impact_summary?.discovered_new_careers || 0}
                color="#2196F3"
              />
            </div>

            {/* Impact Summary */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#FFC107]" />
                Distribution de l'impact
              </h2>
              
              {(() => {
                const discovered = data.impact_indicators.impact_summary?.discovered_new_careers || 0;
                const confirmed = data.impact_indicators.impact_summary?.confirmed_existing_idea || 0;
                const newDirections = data.impact_indicators.impact_summary?.found_new_directions || 0;
                const reflecting = data.impact_indicators.impact_summary?.still_reflecting || 0;
                const total = discovered + confirmed + newDirections + reflecting;
                
                const pct = (val) => total > 0 ? ((val / total) * 100).toFixed(1) : '0.0';
                
                return (
                  <>
                    <p className="text-xs text-[#1D1D1D]/60 mb-3">
                      Basé sur {total} répondant{total > 1 ? 's' : ''} ayant complété le questionnaire post-test
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                        <div className="text-3xl font-bold text-green-600">
                          {pct(discovered)}%
                        </div>
                        <div className="text-sm text-green-700">Ont découvert de nouveaux métiers</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                        <div className="text-3xl font-bold text-blue-600">
                          {pct(confirmed)}%
                        </div>
                        <div className="text-sm text-blue-700">Ont confirmé leur idée</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
                        <div className="text-3xl font-bold text-purple-600">
                          {pct(newDirections)}%
                        </div>
                        <div className="text-sm text-purple-700">Ont trouvé de nouvelles pistes</div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
                        <div className="text-3xl font-bold text-orange-600">
                          {pct(reflecting)}%
                        </div>
                        <div className="text-sm text-orange-700">Sont encore en réflexion</div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </section>

            {/* Regional Impact */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Map className="w-5 h-5 text-[#4CAF50]" />
                Impact par région
              </h2>
              <p className="text-xs text-[#1D1D1D]/60 mb-3">
                Le "Taux impact post-test" représente le % d'utilisateurs ayant répondu au questionnaire post-test avec une découverte de nouveaux métiers.
              </p>
              
              {data.impact_indicators.regional_impact?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E0E0E0]">
                        <th className="text-left py-2 px-3">Région</th>
                        <th className="text-right py-2 px-3">Utilisateurs</th>
                        <th className="text-right py-2 px-3">Taux complétion</th>
                        <th className="text-right py-2 px-3" title="% d'utilisateurs ayant répondu 'découverte' au post-test par rapport au total de la région">
                          Taux impact post-test
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.impact_indicators.regional_impact.map((region, idx) => (
                        <tr key={idx} className="border-b border-[#E0E0E0]/50">
                          <td className="py-2 px-3 font-medium">{region.region}</td>
                          <td className="py-2 px-3 text-right">{region.total_users}</td>
                          <td className="py-2 px-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              region.completion_rate >= 50 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {region.completion_rate}%
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              region.discovery_rate >= 5 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {region.discovery_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données régionales</p>
              )}
            </section>

            {/* Monthly Trends */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#FB6823]" />
                Tendances mensuelles (6 derniers mois)
              </h2>
              
              {data.impact_indicators.monthly_trends?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.impact_indicators.monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_users" fill="#A4187A" name="Utilisateurs" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="completion_rate" fill="#4CAF50" name="% Complétion" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="discovery_rate" fill="#FB6823" name="% Découverte" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données historiques</p>
              )}
            </section>
          </>
        )}

        {/* PART 6: Cross-Analysis Tab */}
        {activeTab === 'cross' && data?.cross_analysis && (
          <>
            {/* Regional Gender Ratios - Only Belgian regions */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#A4187A]" />
                {dashboardLang === 'fr' ? 'Équilibre de genre par région (Belgique)' : 'Genderbalans per regio (België)'}
              </h2>
              
              {(() => {
                // Belgian regions mapping
                const BELGIAN_REGIONS = {
                  'Brussels Capital': 'Bruxelles-Capitale',
                  'Brussels': 'Bruxelles-Capitale',
                  'Bruxelles': 'Bruxelles-Capitale',
                  'Bruxelles-Capitale': 'Bruxelles-Capitale',
                  'Wallonia': 'Wallonie',
                  'Wallonie': 'Wallonie',
                  'Flanders': 'Flandre',
                  'Flandre': 'Flandre',
                  'Vlaanderen': 'Flandre'
                };

                // Filter and normalize to Belgian regions only
                const belgianRegions = (data.cross_analysis.regional_gender_ratios || [])
                  .filter(region => {
                    const regionName = region.region?.toLowerCase() || '';
                    return Object.keys(BELGIAN_REGIONS).some(r => 
                      regionName.includes(r.toLowerCase()) || r.toLowerCase().includes(regionName)
                    );
                  })
                  .map(region => {
                    // Normalize region name
                    let normalizedName = region.region;
                    for (const [key, value] of Object.entries(BELGIAN_REGIONS)) {
                      if (region.region?.toLowerCase().includes(key.toLowerCase())) {
                        normalizedName = value;
                        break;
                      }
                    }
                    return { ...region, region: normalizedName };
                  })
                  // Aggregate duplicates
                  .reduce((acc, region) => {
                    const existing = acc.find(r => r.region === region.region);
                    if (existing) {
                      existing.total += region.total;
                      existing.male_count = (existing.male_count || 0) + (region.male_count || 0);
                      existing.female_count = (existing.female_count || 0) + (region.female_count || 0);
                    } else {
                      acc.push({ ...region });
                    }
                    return acc;
                  }, [])
                  // Recalculate percentages
                  .map(region => {
                    const total = region.total || 1;
                    const male_pct = Math.round(((region.male_count || region.male_pct * total / 100) / total) * 100);
                    const female_pct = 100 - male_pct;
                    const balance_index = 1 - Math.abs(male_pct - female_pct) / 100;
                    return { ...region, male_pct, female_pct, balance_index };
                  });

                if (belgianRegions.length === 0) {
                  return <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données régionales belges</p>;
                }

                return (
                  <div className="space-y-3">
                    {belgianRegions.map((region, idx) => (
                      <div key={idx} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-[#A4187A] flex items-center gap-2">
                            {region.region === 'Bruxelles-Capitale' ? '🏙️' : region.region === 'Wallonie' ? '🌲' : '🌊'}
                            {region.region}
                          </span>
                          <span className="text-sm bg-white px-2 py-1 rounded-full text-[#1D1D1D]/70">{region.total} utilisateurs</span>
                        </div>
                        <div className="flex h-6 rounded-full overflow-hidden bg-gray-200 shadow-inner">
                          <div
                            className="bg-gradient-to-r from-blue-400 to-blue-500 transition-all flex items-center justify-center"
                            style={{ width: `${region.male_pct}%` }}
                            title={`Hommes: ${region.male_pct}%`}
                          >
                            {region.male_pct > 15 && <span className="text-white text-xs font-bold">♂ {region.male_pct}%</span>}
                          </div>
                          <div
                            className="bg-gradient-to-r from-pink-400 to-pink-500 transition-all flex items-center justify-center"
                            style={{ width: `${region.female_pct}%` }}
                            title={`Femmes: ${region.female_pct}%`}
                          >
                            {region.female_pct > 15 && <span className="text-white text-xs font-bold">♀ {region.female_pct}%</span>}
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs mt-2">
                          <span className="text-blue-600 font-medium">Hommes: {region.male_pct}%</span>
                          <span className={`px-3 py-1 rounded-full font-medium ${
                            region.balance_index > 0.7 ? 'bg-green-100 text-green-700' : 
                            region.balance_index > 0.4 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                          }`}>
                            Équilibre: {(region.balance_index * 100).toFixed(0)}%
                          </span>
                          <span className="text-pink-600 font-medium">Femmes: {region.female_pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </section>

            {/* Top Careers by Segment */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FB6823]" />
                {dashboardLang === 'fr' ? 'Métiers populaires par segment (Genre × Âge)' : 'Populaire beroepen per segment (Geslacht × Leeftijd)'}
              </h2>
              
              {data.cross_analysis.top_careers_by_segment && Object.keys(data.cross_analysis.top_careers_by_segment).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(data.cross_analysis.top_careers_by_segment).slice(0, 6).map(([segment, careers], idx) => {
                    const [gender, age] = segment.split('_');
                    const genderLabel = gender === 'male' ? '♂ Hommes' : gender === 'female' ? '♀ Femmes' : '⚪ Autres';
                    return (
                      <div key={idx} className={`rounded-lg p-4 border ${
                        gender === 'male' ? 'bg-blue-50 border-blue-200' : 
                        gender === 'female' ? 'bg-pink-50 border-pink-200' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <h3 className="font-medium text-sm mb-2">{genderLabel} • {age}</h3>
                        <ul className="space-y-1">
                          {careers.map((c, i) => (
                            <li key={i} className="text-xs flex justify-between">
                              <span className="truncate mr-2">{c.career}</span>
                              <span className="text-[#1D1D1D]/50">{c.count}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données</p>
              )}
            </section>

            {/* Segments Analyzed */}
            <div className="bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-xl p-4 border border-[#A4187A]/20">
              <p className="text-center text-sm text-[#1D1D1D]/60">
                <span className="font-bold text-[#A4187A] text-lg">{data.cross_analysis.segments_analyzed || 0}</span> segments démographiques analysés
              </p>
            </div>
          </>
        )}

        {/* PART 7: Temporal Trends Tab */}
        {activeTab === 'temporal' && data?.temporal_trends && (
          <>
            {/* Daily Patterns */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#A4187A]" />
                {dashboardLang === 'fr' ? 'Activité par jour de la semaine' : 'Activiteit per dag van de week'}
              </h2>
              
              {data.temporal_trends.daily_patterns?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.temporal_trends.daily_patterns}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total" fill="#A4187A" name="Total" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="completed" fill="#4CAF50" name="Complétés" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données</p>
              )}
            </section>

            {/* Hourly Patterns */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#FB6823]" />
                {dashboardLang === 'fr' ? 'Activité par heure de la journée' : 'Activiteit per uur van de dag'}
              </h2>
              
              {data.temporal_trends.hourly_patterns?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.temporal_trends.hourly_patterns}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={2} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FB6823" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Peak Hours */}
                  {data.temporal_trends.peak_hours?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2 justify-center">
                      <span className="text-sm text-[#1D1D1D]/60">Heures de pointe:</span>
                      {data.temporal_trends.peak_hours.map((peak, idx) => (
                        <span key={idx} className="px-3 py-1 bg-[#FB6823]/10 text-[#FB6823] rounded-full text-sm font-medium">
                          {peak.label} ({peak.count})
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données</p>
              )}
            </section>

            {/* Domain Evolution */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#4CAF50]" />
                {dashboardLang === 'fr' ? 'Évolution des domaines (6 mois)' : 'Evolutie van domeinen (6 maanden)'}
              </h2>
              
              {data.temporal_trends.domain_evolution?.length > 0 ? (
                <div className="space-y-4">
                  {data.temporal_trends.domain_evolution.slice(0, 6).map((domain, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium capitalize">{domain.domain}</span>
                        <span className="text-xs text-[#1D1D1D]/50">
                          {domain.data?.reduce((sum, d) => sum + d.count, 0) || 0} total
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {domain.data?.map((monthData, mIdx) => (
                          <div
                            key={mIdx}
                            className="flex-1 rounded"
                            style={{
                              height: '24px',
                              backgroundColor: `rgba(106, 13, 173, ${Math.min(monthData.count / 20, 1)})`
                            }}
                            title={`${monthData.month}: ${monthData.count}`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#1D1D1D]/60 text-center py-4">Pas assez de données historiques</p>
              )}
            </section>
          </>
        )}

        {/* PART 8-9: Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <>
            {/* Correlation Insights */}
            {data?.correlations && (
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-[#A4187A]" />
                  {dashboardLang === 'fr' ? 'Corrélations clés' : 'Belangrijke correlaties'}
                </h2>
                
                {/* Confidence vs Outcomes */}
                {data.correlations.confidence_outcomes?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-[#1D1D1D]/70 mb-3">
                      Impact du niveau de confiance initial
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#E0E0E0]">
                            <th className="text-left py-2 px-3">Avant le test</th>
                            <th className="text-right py-2 px-3">Découverte</th>
                            <th className="text-right py-2 px-3">Confirmé</th>
                            <th className="text-right py-2 px-3">Nouvelles pistes</th>
                            <th className="text-right py-2 px-3">Réflexion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.correlations.confidence_outcomes.map((item, idx) => (
                            <tr key={idx} className="border-b border-[#E0E0E0]/50">
                              <td className="py-2 px-3 font-medium">{item.pre_test}</td>
                              <td className="py-2 px-3 text-right text-green-600">{item.discovered_pct}%</td>
                              <td className="py-2 px-3 text-right text-blue-600">{item.confirmed_pct}%</td>
                              <td className="py-2 px-3 text-right text-purple-600">{item.new_directions_pct}%</td>
                              <td className="py-2 px-3 text-right text-orange-600">{item.reflecting_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Common Domain Pairs */}
                {data.correlations.common_domain_pairs?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-[#1D1D1D]/70 mb-3">
                      Paires de domaines fréquentes
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {data.correlations.common_domain_pairs.map((pair, idx) => (
                        <div key={idx} className="px-3 py-2 bg-purple-50 rounded-lg border border-purple-200">
                          <span className="text-sm">
                            <span className="font-medium capitalize">{pair.domain1}</span>
                            <span className="mx-2 text-[#1D1D1D]/40">+</span>
                            <span className="font-medium capitalize">{pair.domain2}</span>
                          </span>
                          <span className="ml-2 text-xs text-purple-600">({pair.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completion Predictors */}
                {data.correlations.completion_predictors?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-[#1D1D1D]/70 mb-3">
                      Meilleurs profils de complétion Stage 2
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {data.correlations.completion_predictors.slice(0, 5).map((pred, idx) => (
                        <div key={idx} className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                          <div className="text-lg font-bold text-green-600">{pred.completion_rate}%</div>
                          <div className="text-xs text-[#1D1D1D]/60">
                            {pred.gender === 'male' ? '♂' : pred.gender === 'female' ? '♀' : '⚪'} {pred.age}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Strategic Recommendations */}
            {data?.strategic_recommendations?.length > 0 && (
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-[#FFC107]" />
                  {dashboardLang === 'fr' ? 'Recommandations stratégiques' : 'Strategische aanbevelingen'}
                </h2>
                
                <div className="space-y-4">
                  {data.strategic_recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg p-4 border-l-4 ${
                        rec.priority === 'critical' ? 'bg-red-50 border-red-500' :
                        rec.priority === 'high' ? 'bg-orange-50 border-orange-500' :
                        rec.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                        'bg-blue-50 border-blue-500'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              rec.priority === 'critical' ? 'bg-red-200 text-red-800' :
                              rec.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                              rec.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {rec.priority === 'critical' ? 'CRITIQUE' :
                               rec.priority === 'high' ? 'HAUTE' :
                               rec.priority === 'medium' ? 'MOYENNE' : 'BASSE'}
                            </span>
                            <span className="text-xs text-[#1D1D1D]/50">{rec.target}</span>
                          </div>
                          <h3 className="font-medium text-[#1D1D1D]">{rec.title}</h3>
                          <p className="text-sm text-[#1D1D1D]/70 mt-1">{rec.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-[#1D1D1D]/50">Métrique</span>
                          <div className="font-bold text-[#A4187A]">{rec.metric}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* No recommendations fallback */}
            {(!data?.strategic_recommendations || data.strategic_recommendations.length === 0) && (
              <div className="bg-green-50 rounded-xl p-8 text-center border border-green-200">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="font-bold text-green-800 mb-2">Excellent!</h3>
                <p className="text-green-600">
                  Tous les indicateurs sont dans les normes. Aucune recommandation urgente.
                </p>
              </div>
            )}
          </>
        )}

      </main>
      
      {/* Footer */}
      <footer className="bg-[#1D1D1D] text-white py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-white/60">
          Observatoire National des Aspirations Professionnelles · Déclic Belgium · {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

// Helper components
function MetricCard({ icon: Icon, label, value, color, subtitle }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1D1D1D]">{value}</p>
          <p className="text-xs text-[#1D1D1D]/60">{label}</p>
          {subtitle && <p className="text-[10px] text-[#1D1D1D]/40 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}
